import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, Cpu, Zap, Network, Terminal, Github, ExternalLink } from "lucide-react";

const WORKER_REPO = "https://github.com/aabbiirr/prepcheck-contribute";
const WORKER_ZIP = `${WORKER_REPO}/archive/refs/heads/main.zip`;

export default function DesktopWorker() {
    const [stats, setStats] = useState({ total_nodes: 0, online_nodes: 0, worker_questions: 0, total_questions: 0 });
    const [leaderboard, setLeaderboard] = useState([]);

    useEffect(() => {
        const fetch = () => {
            api.get("/worker/public-stats").then((r) => setStats(r.data));
            api.get("/worker/leaderboard").then((r) => setLeaderboard(r.data));
        };
        fetch();
        const t = setInterval(fetch, 6000);
        return () => clearInterval(t);
    }, []);

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
            <span className="overline text-primary">▮ Distributed compute</span>
            <h1 className="mt-2 font-heading font-black text-4xl sm:text-6xl tracking-tighter max-w-4xl">
                Donate your idle CPU. <br />
                <span className="text-primary">Help build the bank.</span>
            </h1>
            <p className="mt-4 max-w-2xl text-muted-foreground">
                Our BOINC-style desktop worker runs when your machine is idle, pulls a question-generation job, calls the LLM, and ships the questions back to the vector bank. Every student on this platform is one step closer to a top rank because of contributors like you.
            </p>

            <div className="mt-10 grid md:grid-cols-4 gap-4 stagger-in">
                <StatCard icon={<Network />} label="Total nodes" value={stats.total_nodes} />
                <StatCard icon={<Zap />} label="Online now" value={stats.online_nodes} accent />
                <StatCard icon={<Cpu />} label="Questions by workers" value={stats.worker_questions} />
                <StatCard icon={<Terminal />} label="Bank size" value={stats.total_questions} />
            </div>

            <div className="mt-12 grid lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 border border-border rounded-sm p-6">
                    <h2 className="font-heading font-bold text-2xl">Get the worker app</h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                        Cross-platform Electron client (Windows / macOS / Linux). Runs quietly in the background, respects your CPU limits, uses your own Ollama, DeepSeek, OpenAI, or Claude credentials, and reports live to the platform.
                    </p>

                    <pre className="mt-6 border border-border rounded-sm bg-muted/50 p-4 text-xs font-mono overflow-x-auto">
{`# Clone & run the worker locally
$ git clone ${WORKER_REPO}.git
$ cd prepcheck-contribute
$ npm install
$ export PREPCHECK_API=https://prepcheck.illumeai.in

# Choose one AI provider
$ export PREPCHECK_LLM_PROVIDER=ollama
$ export PREPCHECK_LLM_BASE_URL=http://localhost:11434/v1
$ export PREPCHECK_LLM_MODEL=llama3.1

# Or use: openai, deepseek, anthropic
# $ export PREPCHECK_LLM_API_KEY=your-key
$ npm start

# The worker will:
#   1. POST /api/worker/register           → get node_id + node_secret
#   2. POST /api/worker/heartbeat  (30s)   → report CPU/RAM
#   3. POST /api/worker/pull-job           → grab a job
#   4. run local LLM inference             → generate questions
#   5. POST /api/worker/submit-result      → contribute to bank`}
                    </pre>

                    <div className="mt-4 flex flex-wrap gap-2">
                        <a href={WORKER_ZIP}>
                            <Button data-testid="worker-download-btn" className="rounded-sm bg-primary hover:bg-primary/90 btn-press">
                                <Download className="w-4 h-4 mr-2" /> Download source
                            </Button>
                        </a>
                        <a href={WORKER_REPO} target="_blank" rel="noreferrer">
                            <Button variant="outline" className="rounded-sm btn-press">
                                <Github className="w-4 h-4 mr-2" /> View on GitHub <ExternalLink className="w-3 h-3 ml-2" />
                            </Button>
                        </a>
                    </div>
                </div>

                <div className="border border-border rounded-sm p-6">
                    <h3 className="font-heading font-bold text-lg">Leaderboard</h3>
                    <div className="mt-4 space-y-2">
                        {leaderboard.length === 0 && (
                            <p className="text-sm text-muted-foreground">Be the first node to contribute.</p>
                        )}
                        {leaderboard.map((n, i) => (
                            <div key={n.id} className="flex items-center justify-between border-b border-border py-2 text-sm">
                                <div className="font-mono">
                                    <span className="text-primary">#{i + 1}</span> {n.hostname}
                                    <div className="text-[10px] text-muted-foreground uppercase">{n.platform} · {n.cpu_cores}c</div>
                                </div>
                                <span className="font-mono font-bold">{n.questions_contributed}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

const StatCard = ({ icon, label, value, accent }) => (
    <div className="border border-border rounded-sm p-6">
        <div className="text-primary">{React.cloneElement(icon, { className: "w-5 h-5" })}</div>
        <div className={`font-heading font-black text-3xl mt-4 font-mono ${accent ? "text-primary" : ""}`}>{value}</div>
        <div className="overline text-muted-foreground text-[9px] mt-1">{label}</div>
    </div>
);
